import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus, Search, Scan, AlertTriangle, Check, X, Upload, Download, Shuffle, ChevronsRight, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/lib/api";
import { useHasPerm } from "@/components/PermissionGate";

const EMPTY = {
  fornitore_id: "", nome_articolo: "", codice_barre: "", codice_articolo_fornitore: "", categoria_id: "",
  soglia_minima: "", deposito_id: "", quantita: "",
  prezzo_acquisto_con_iva: "", iva_acquisto: 22, prezzo_acquisto: "",
  prezzo_vendita_con_iva: "", iva_vendita: 22, prezzo_vendita: "",
  ricarico: "", minimo_ordine: "",
  unita_misura_id: "", pezzi_per_confezione: "", nota: "",
};

// utility: net = gross / (1 + iva/100)
const netFromGross = (gross, iva) => {
  const i = parseFloat(iva) || 0;
  const g = parseFloat(gross) || 0;
  return +(g / (1 + i / 100)).toFixed(4);
};
const grossFromNet = (net, iva) => {
  const i = parseFloat(iva) || 0;
  const n = parseFloat(net) || 0;
  return +(n * (1 + i / 100)).toFixed(4);
};
const numVal = (v) => (v === "" || v === null || v === undefined ? "" : v);

export default function Prodotti() {
  const hasPerm = useHasPerm();
  const canCreate = hasPerm("prodotti:create");
  const canEdit = hasPerm("prodotti:edit");
  const canDelete = hasPerm("prodotti:delete");
  const canImport = hasPerm("prodotti:import");
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // Multi-deposit mode for product creation: array of {deposito_id, quantita}
  const [multiDepositi, setMultiDepositi] = useState([]); // [] => single-deposit mode
  const [search, setSearch] = useState("");
  const [filterFornitore, setFilterFornitore] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const filterMode = searchParams.get("filter") || ""; // "esaurimento" or ""

  const clearEsaurimento = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };
  const [sortBy, setSortBy] = useState("nome_articolo");
  const [sortDir, setSortDir] = useState("asc");
  const [deleteId, setDeleteId] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { id, field, value }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdateResult, setBulkUpdateResult] = useState(null);
  const [bulkUpdateBusy, setBulkUpdateBusy] = useState(false);
  const [multiMode, setMultiMode] = useState(false);
  // Quick-add supplier
  const [quickSupplierOpen, setQuickSupplierOpen] = useState(false);
  const [quickSupplier, setQuickSupplier] = useState({ ragione_sociale: "", nome: "", email: "", telefono: "" });
  const [quickSupplierBusy, setQuickSupplierBusy] = useState(false);

  // Sorted (alphabetical) versions for selects + display
  const suppliersSorted = useMemo(
    () => [...suppliers].sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale, "it")),
    [suppliers]
  );
  const depositsSorted = useMemo(
    () => [...deposits].sort((a, b) => a.nome.localeCompare(b.nome, "it")),
    [deposits]
  );

  // Base storage units only — excludes any unit whose name starts with "confez" (confezione is a multiplier, not a stock unit)
  const isConfezioneUnit = (name) => (name || "").trim().toLowerCase().startsWith("confez");
  const baseUnits = useMemo(
    () => units.filter((u) => !isConfezioneUnit(u.nome)),
    [units]
  );

  const load = async () => {
    setLoading(true);
    try {
      // Use allSettled so a user with prodotti:view but without fornitori:view (or similar)
      // still sees products; missing lookups just show as "—" in the UI.
      const results = await Promise.allSettled([
        api.get("/products"),
        api.get("/suppliers"),
        api.get("/deposits"),
        api.get("/categories"),
        api.get("/units"),
      ]);
      const [p, s, d, c, u] = results;
      if (p.status === "rejected") {
        // Only fail hard if the primary resource (products) failed.
        toast.error("Errore caricamento prodotti");
      } else {
        setList(p.value.data);
      }
      setSuppliers(s.status === "fulfilled" ? s.value.data : []);
      setDeposits(d.status === "fulfilled" ? d.value.data : []);
      setCategories(c.status === "fulfilled" ? c.value.data : []);
      setUnits(u.status === "fulfilled" ? u.value.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    const preferred = deposits.find((x) => x.preferito);
    setEditing(null);
    setForm({
      ...EMPTY,
      deposito_id: preferred?.id || depositsSorted[0]?.id || "",
    });
    setMultiDepositi([]);
    setMultiMode(false);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    // Normalize legacy pezzi_per_confezione: 0/1 → empty (not orderable / trivial)
    const ppc = p.pezzi_per_confezione;
    const normalized = { ...EMPTY, ...p, pezzi_per_confezione: ppc && ppc > 1 ? ppc : "" };
    setForm(normalized);
    setMultiDepositi([]);
    setMultiMode(false);
    setDialogOpen(true);
  };

  const updateForm = (changes) => {
    setForm((prev) => {
      const next = { ...prev, ...changes };
      const pa = parseFloat(next.prezzo_acquisto) || 0;
      const pv = parseFloat(next.prezzo_vendita) || 0;
      if (pa > 0 && pv > 0) {
        next.ricarico = +(((pv - pa) / pa) * 100).toFixed(2);
      } else {
        next.ricarico = "";
      }
      return next;
    });
  };

  const onPriceField = (key, value) => {
    const v = parseFloat(value);
    const num = isNaN(v) ? "" : v;
    if (key === "prezzo_acquisto_con_iva") {
      updateForm({
        prezzo_acquisto_con_iva: num,
        prezzo_acquisto: num === "" ? "" : netFromGross(num, form.iva_acquisto),
      });
    } else if (key === "prezzo_acquisto") {
      updateForm({
        prezzo_acquisto: num,
        prezzo_acquisto_con_iva: num === "" ? "" : grossFromNet(num, form.iva_acquisto),
      });
    } else if (key === "iva_acquisto") {
      updateForm({
        iva_acquisto: num,
        prezzo_acquisto: form.prezzo_acquisto_con_iva === "" ? "" : netFromGross(form.prezzo_acquisto_con_iva, num),
      });
    } else if (key === "prezzo_vendita_con_iva") {
      updateForm({
        prezzo_vendita_con_iva: num,
        prezzo_vendita: num === "" ? "" : netFromGross(num, form.iva_vendita),
      });
    } else if (key === "prezzo_vendita") {
      updateForm({
        prezzo_vendita: num,
        prezzo_vendita_con_iva: num === "" ? "" : grossFromNet(num, form.iva_vendita),
      });
    } else if (key === "iva_vendita") {
      updateForm({
        iva_vendita: num,
        prezzo_vendita: form.prezzo_vendita_con_iva === "" ? "" : netFromGross(form.prezzo_vendita_con_iva, num),
      });
    }
  };

  // Toggle a deposit in multi-deposit mode
  const toggleMultiDeposit = (depId) => {
    setMultiDepositi((prev) => {
      const found = prev.find((x) => x.deposito_id === depId);
      if (found) return prev.filter((x) => x.deposito_id !== depId);
      return [...prev, { deposito_id: depId, quantita: "" }];
    });
  };

  const setMultiDepQty = (depId, q) => {
    setMultiDepositi((prev) => prev.map((x) => (x.deposito_id === depId ? { ...x, quantita: q } : x)));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Required fields
    const requiredErr = (() => {
      if (!form.nome_articolo) return "Nome articolo obbligatorio";
      if (!form.categoria_id) return "Categoria obbligatoria";
      if (!form.unita_misura_id) return "Unità di misura base obbligatoria";
      const selectedUnit = units.find((u) => u.id === form.unita_misura_id);
      if (selectedUnit && isConfezioneUnit(selectedUnit.nome)) {
        return "L'unità di misura base non può essere 'Confezione' (è un moltiplicatore, non un'unità di stoccaggio)";
      }
      if (form.prezzo_acquisto === "" || form.prezzo_acquisto === null) return "Prezzo di acquisto senza IVA obbligatorio";
      // multi vs single deposit
      if (!editing && multiMode) {
        if (multiDepositi.length === 0) return "Seleziona almeno un deposito";
        if (multiDepositi.some((d) => d.quantita === "" || d.quantita === null)) return "Quantità obbligatoria per ogni deposito selezionato";
      } else {
        if (!form.deposito_id) return "Deposito obbligatorio";
        if (form.quantita === "" || form.quantita === null) return "Quantità obbligatoria";
        // in edit + multimode, also validate extra deposits
        if (editing && multiMode && multiDepositi.some((d) => d.deposito_id !== form.deposito_id && (d.quantita === "" || d.quantita === null))) {
          return "Quantità obbligatoria per ogni nuovo deposito selezionato";
        }
      }
      return null;
    })();
    if (requiredErr) {
      toast.error(requiredErr);
      return;
    }

    const basePayload = (q, deposito_id) => ({
      ...form,
      fornitore_id: form.fornitore_id || null,
      deposito_id,
      quantita: parseFloat(q) || 0,
      soglia_minima: form.soglia_minima === "" ? 0 : parseFloat(form.soglia_minima) || 0,
      prezzo_acquisto_con_iva: parseFloat(form.prezzo_acquisto_con_iva) || 0,
      prezzo_acquisto: parseFloat(form.prezzo_acquisto) || 0,
      prezzo_vendita_con_iva: form.prezzo_vendita_con_iva === "" ? 0 : parseFloat(form.prezzo_vendita_con_iva) || 0,
      prezzo_vendita: form.prezzo_vendita === "" ? 0 : parseFloat(form.prezzo_vendita) || 0,
      iva_acquisto: parseFloat(form.iva_acquisto) || 0,
      iva_vendita: parseFloat(form.iva_vendita) || 0,
      ricarico: form.ricarico === "" || form.ricarico === null || form.ricarico === undefined ? 0 : parseFloat(form.ricarico) || 0,
      minimo_ordine: form.minimo_ordine === "" ? 0 : parseFloat(form.minimo_ordine) || 0,
      pezzi_per_confezione: (() => {
        const v = form.pezzi_per_confezione;
        if (v === "" || v === null || v === undefined) return 0;
        const n = parseInt(v);
        return !isNaN(n) && n > 0 ? n : 0;
      })(),
    });

    try {
      if (editing) {
        // First update the current product row
        await api.put(`/products/${editing.id}`, basePayload(form.quantita, form.deposito_id));
        // If multiMode is enabled in edit, also create NEW rows for other deposits
        if (multiMode && multiDepositi.length > 0) {
          const newDeps = multiDepositi.filter((d) => d.deposito_id !== form.deposito_id);
          if (newDeps.length > 0) {
            const results = await Promise.allSettled(
              newDeps.map((d) => api.post("/products", basePayload(d.quantita, d.deposito_id)))
            );
            const ok = results.filter((r) => r.status === "fulfilled").length;
            if (ok > 0) toast.success(`Prodotto aggiornato + creato in ${ok} altri deposit${ok > 1 ? "i" : "o"}`);
            const failures = results
              .map((r, i) => ({ r, dep: newDeps[i] }))
              .filter(({ r }) => r.status === "rejected");
            failures.forEach(({ r, dep }) => {
              const depName = deposits.find((x) => x.id === dep.deposito_id)?.nome || dep.deposito_id;
              const reason = formatApiErrorDetail(r.reason?.response?.data?.detail) || r.reason?.message || "Errore sconosciuto";
              toast.error(`Deposito "${depName}": ${reason}`);
            });
          } else {
            toast.success("Prodotto aggiornato");
          }
        } else {
          toast.success("Prodotto aggiornato");
        }
      } else if (multiMode && multiDepositi.length > 0) {
        const results = await Promise.allSettled(
          multiDepositi.map((d) => api.post("/products", basePayload(d.quantita, d.deposito_id)))
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const failures = results
          .map((r, i) => ({ r, dep: multiDepositi[i] }))
          .filter(({ r }) => r.status === "rejected");
        if (ok > 0) toast.success(`${ok} prodotti creati in ${ok} deposit${ok > 1 ? "i" : "o"}`);
        failures.forEach(({ r, dep }) => {
          const depName = deposits.find((x) => x.id === dep.deposito_id)?.nome || dep.deposito_id;
          const reason = formatApiErrorDetail(r.reason?.response?.data?.detail) || r.reason?.message || "Errore sconosciuto";
          toast.error(`Deposito "${depName}": ${reason}`);
        });
        if (failures.length > 0 && ok === 0) return; // do not close dialog if everything failed
      } else {
        await api.post("/products", basePayload(form.quantita, form.deposito_id));
        toast.success("Prodotto creato");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Prodotto eliminato");
      setDeleteId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitQuickSupplier = async (e) => {
    e.preventDefault();
    if (!quickSupplier.ragione_sociale || !quickSupplier.nome || !quickSupplier.email || !quickSupplier.telefono) {
      toast.error("Compila ragione sociale, nome, email e telefono");
      return;
    }
    setQuickSupplierBusy(true);
    try {
      const { data } = await api.post("/suppliers", { ...quickSupplier });
      toast.success("Fornitore creato");
      const reload = await api.get("/suppliers");
      setSuppliers(reload.data);
      updateForm({ fornitore_id: data.id });
      setQuickSupplierOpen(false);
      setQuickSupplier({ ragione_sociale: "", nome: "", email: "", telefono: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore creazione");
    } finally {
      setQuickSupplierBusy(false);
    }
  };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/products/bulk-delete", { ids: Array.from(selectedIds) });
      toast.success(`${data.deleted} prodotti eliminati`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const authedDownload = async (path, fileName) => {
    try {
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Errore download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(err.message); }
  };
  const downloadTemplate = () => authedDownload("/products/template", "template_prodotti.xlsx");
  const downloadExport = () => authedDownload("/products/export", "prodotti_export.xlsx");

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/products/import`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setImportResult(data);
      toast.success(`${data.created} prodotti importati`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} righe saltate`);
      await load();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Errore import");
    } finally {
      setImportBusy(false);
      e.target.value = "";
    }
  };

  const handleBulkUpdate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUpdateBusy(true);
    setBulkUpdateResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/products/bulk-update`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setBulkUpdateResult(data);
      toast.success(`${data.updated} prodotti aggiornati`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} righe saltate`);
      await load();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Errore aggiornamento massivo");
    } finally {
      setBulkUpdateBusy(false);
      e.target.value = "";
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (rows) => {
    setSelectedIds((prev) => {
      const allIds = rows.flatMap((r) => r.products.map((p) => p.id));
      if (allIds.every((id) => prev.has(id))) return new Set();
      return new Set(allIds);
    });
  };

  const saveCell = async () => {
    if (!editingCell) return;
    try {
      const payload = { [editingCell.field]: editingCell.value };
      if (editingCell.field === "prezzo_vendita_con_iva") {
        const prod = list.find((p) => p.id === editingCell.id);
        const iva = prod?.iva_vendita ?? 22;
        payload.prezzo_vendita = netFromGross(editingCell.value, iva);
      }
      if (editingCell.field === "prezzo_acquisto") {
        const prod = list.find((p) => p.id === editingCell.id);
        const iva = prod?.iva_acquisto ?? 22;
        payload.prezzo_acquisto_con_iva = +(parseFloat(editingCell.value) * (1 + iva / 100)).toFixed(4);
      }
      if (editingCell.field === "fornitore_id") {
        payload.fornitore_id = editingCell.value || null;
      }
      await api.put(`/products/${editingCell.id}`, payload);
      toast.success("Aggiornato");
      setEditingCell(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Aggregation: group products by `nome_articolo + fornitore_id`
  const grouped = useMemo(() => {
    const map = new Map();
    list.forEach((p) => {
      const key = `${(p.nome_articolo || "").trim().toLowerCase()}::${p.fornitore_id || ""}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    return Array.from(map.entries()).map(([key, products]) => ({
      key,
      products,
      primary: products[0],
      totalQta: products.reduce((s, p) => s + (Number(p.quantita) || 0), 0),
    }));
  }, [list]);

  const filtered = useMemo(() => {
    let arr = grouped;
    if (filterMode === "esaurimento") {
      arr = arr.filter((g) => (g.totalQta || 0) <= (g.primary?.soglia_minima || 0));
    }
    if (filterFornitore && filterFornitore !== "all") {
      const wanted = filterFornitore === "none" ? "" : filterFornitore;
      arr = arr.filter((g) => g.products.some((p) => (p.fornitore_id || "") === wanted));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const catNameOf = (id) => (categories.find((c) => c.id === id)?.nome || "").toLowerCase();
      arr = arr.filter((g) =>
        g.products.some((p) =>
          [p.nome_articolo, p.codice_barre, p.nota, catNameOf(p.categoria_id)]
            .some((v) => v && String(v).toLowerCase().includes(q))
        )
      );
    }
    const getKey = (g) => {
      const p = g.primary;
      switch (sortBy) {
        case "nome_articolo": return (p.nome_articolo || "").toLowerCase();
        case "unita": return (units.find((u) => u.id === p.unita_misura_id)?.nome || "").toLowerCase();
        case "quantita": return g.totalQta || 0;
        case "soglia_minima": return Number(p.soglia_minima) || 0;
        case "prezzo": return Number(p.prezzo_vendita_con_iva) || 0;
        case "prezzo_acquisto": return Number(p.prezzo_acquisto) || 0;
        case "deposito": return g.products.length > 1
          ? `zzz${g.products.length}`
          : (deposits.find((d) => d.id === p.deposito_id)?.nome || "").toLowerCase();
        case "codice_barre": return (p.codice_barre || "").toLowerCase();
        case "pezzi": return Number(p.pezzi_per_confezione) || 0;
        case "fornitore": return (suppliers.find((s) => s.id === p.fornitore_id)?.ragione_sociale || "").toLowerCase();
        case "nota": return (p.nota || "").toLowerCase();
        default: return "";
      }
    };
    const sorted = [...arr].sort((a, b) => {
      const va = getKey(a);
      const vb = getKey(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [grouped, search, filterFornitore, filterMode, sortBy, sortDir, units, deposits, suppliers, categories]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown size={12} className="inline ml-1 opacity-30" />;
    return sortDir === "asc"
      ? <ChevronUp size={12} className="inline ml-1" style={{ color: "var(--ds-primary)" }} />
      : <ChevronDown size={12} className="inline ml-1" style={{ color: "var(--ds-primary)" }} />;
  };

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.ragione_sociale || "—";
  const depositName = (id) => deposits.find((d) => d.id === id)?.nome || "—";
  const unitName = (id) => units.find((u) => u.id === id)?.nome || "—";

  return (
    <div className="space-y-6" data-testid="prodotti-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-tag mb-2">Catalogo</div>
          <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Prodotti</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
            Gestione articoli con calcolo automatico IVA e ricarico.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 && canDelete && (
            <Button
              onClick={() => setBulkDeleteOpen(true)}
              variant="outline"
              className="rounded-lg"
              style={{ borderColor: "var(--ds-danger)", color: "var(--ds-danger)" }}
              data-testid="prodotti-bulk-delete-button"
            >
              <Trash2 size={14} className="mr-1.5" /> Elimina {selectedIds.size}
            </Button>
          )}
          <Button onClick={downloadExport} variant="outline" className="rounded-lg" data-testid="prodotti-export-button">
            <Download size={14} className="mr-1.5" /> Esporta XLS
          </Button>
          {canEdit && (
            <Button onClick={() => { setBulkUpdateOpen(true); setBulkUpdateResult(null); }} variant="outline" className="rounded-lg" data-testid="prodotti-bulk-update-button">
              <Upload size={14} className="mr-1.5" /> Modifica massiva
            </Button>
          )}
          <Button onClick={downloadTemplate} variant="outline" className="rounded-lg" data-testid="prodotti-template-button">
            <Download size={14} className="mr-1.5" /> Template
          </Button>
          {canImport && (
            <Button onClick={() => { setImportOpen(true); setImportResult(null); }} variant="outline" className="rounded-lg" data-testid="prodotti-import-button">
              <Upload size={14} className="mr-1.5" /> Importa XLS
            </Button>
          )}
          {canCreate && (
            <Button onClick={openCreate} className="ds-btn-primary" data-testid="prodotto-add-button">
              <Plus size={16} className="mr-1.5" /> Aggiungi prodotto
            </Button>
          )}
        </div>
      </div>

      <div className="ds-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-text-secondary)" }} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, codice, categoria, nota…" className="pl-9 rounded-lg" data-testid="prodotti-search" />
          </div>
          <div className="min-w-[200px]">
            <Select value={filterFornitore} onValueChange={setFilterFornitore}>
              <SelectTrigger className="rounded-lg" data-testid="prodotti-filter-fornitore">
                <SelectValue placeholder="Filtra fornitore…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i fornitori</SelectItem>
                <SelectItem value="none">— Nessun fornitore —</SelectItem>
                {[...suppliers].sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale, "it")).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.ragione_sociale}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
            {filtered.length} articoli ({list.length} righe)
          </div>
        </div>

        {filterMode === "esaurimento" && (
          <div
            className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg text-sm"
            style={{ background: "rgba(139,58,54,0.08)", border: "1px solid rgba(139,58,54,0.2)", color: "#8B3A36" }}
            data-testid="prodotti-esaurimento-banner"
          >
            <div>
              <b>Filtro attivo:</b> mostro solo prodotti <b>in esaurimento</b> (giacenza ≤ soglia minima).
            </div>
            <button
              type="button"
              onClick={clearEsaurimento}
              className="underline text-xs"
              data-testid="prodotti-clear-esaurimento"
            >
              Rimuovi filtro
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      filtered.length > 0 &&
                      filtered.every((g) => g.products.every((p) => selectedIds.has(p.id)))
                    }
                    onCheckedChange={() => toggleSelectAll(filtered)}
                    data-testid="prodotti-select-all"
                  />
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("nome_articolo")} data-testid="sort-nome">Nome articolo<SortIcon col="nome_articolo" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("unita")} data-testid="sort-unita">U.M.<SortIcon col="unita" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("pezzi")} data-testid="sort-pezzi">P/Conf.<SortIcon col="pezzi" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("quantita")} data-testid="sort-quantita">Q.tà<SortIcon col="quantita" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("soglia_minima")} data-testid="sort-soglia">Soglia<SortIcon col="soglia_minima" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("prezzo_acquisto")} data-testid="sort-pacq">Prezzo acq.<SortIcon col="prezzo_acquisto" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("prezzo")} data-testid="sort-prezzo">Prezzo vend.<SortIcon col="prezzo" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("deposito")} data-testid="sort-deposito">Magazzino<SortIcon col="deposito" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("codice_barre")} data-testid="sort-codice">Codice<SortIcon col="codice_barre" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("fornitore")} data-testid="sort-fornitore">Fornitore<SortIcon col="fornitore" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("nota")} data-testid="sort-nota">Nota<SortIcon col="nota" /></TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Caricamento…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun prodotto</TableCell></TableRow>
              )}
              {filtered.map((g) => {
                const p = g.primary;
                const isMulti = g.products.length > 1;
                const isLow = (g.totalQta || 0) <= (p.soglia_minima || 0);
                const isSelected = g.products.every((x) => selectedIds.has(x.id));
                return (
                  <TableRow key={g.key} data-testid={`prodotto-row-${p.id}`} data-selected={isSelected || undefined}>
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            const allIn = g.products.every((x) => next.has(x.id));
                            if (allIn) g.products.forEach((x) => next.delete(x.id));
                            else g.products.forEach((x) => next.add(x.id));
                            return next;
                          });
                        }}
                        data-testid={`prodotto-select-${p.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Link to={`/prodotti/${p.id}`} className="hover:underline" style={{ color: "var(--ds-primary)" }} data-testid={`prodotto-link-${p.id}`}>
                          {p.nome_articolo}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{unitName(p.unita_misura_id)}</TableCell>
                    <TableCell className="text-sm">{p.pezzi_per_confezione && p.pezzi_per_confezione > 1 ? p.pezzi_per_confezione : "—"}</TableCell>
                    <TableCell>
                      {isMulti ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="font-medium flex items-center gap-1 hover:underline disabled:no-underline disabled:cursor-default"
                              style={{ color: isLow ? "var(--ds-danger)" : "var(--ds-text-primary)" }}
                              title={canEdit ? "Quantità totale — clic per modificare per deposito" : "Quantità totale"}
                              data-testid={`prodotto-edit-qta-multi-${p.id}`}
                              disabled={!canEdit}
                            >
                              {g.totalQta}
                              {isLow && <AlertTriangle size={12} />}
                              {canEdit && <Pencil size={11} className="opacity-40" />}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-72 p-3">
                            <div className="label-tag mb-2">Modifica quantità per deposito</div>
                            <div className="space-y-2">
                              {g.products.map((sp) => (
                                <div key={sp.id} className="grid grid-cols-[1fr_90px_36px] gap-2 items-center">
                                  <span className="text-sm truncate">{depositName(sp.deposito_id)}</span>
                                  <Input
                                    type="number"
                                    step="any"
                                    defaultValue={sp.quantita}
                                    className="h-8 rounded-md"
                                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                    onBlur={async (e) => {
                                      const newVal = parseFloat(e.target.value);
                                      if (isNaN(newVal) || newVal === sp.quantita) return;
                                      try {
                                        await api.put(`/products/${sp.id}`, { quantita: newVal });
                                        toast.success(`${depositName(sp.deposito_id)}: ${newVal}`);
                                        await load();
                                      } catch (err) {
                                        toast.error(formatApiErrorDetail(err.response?.data?.detail));
                                      }
                                    }}
                                    data-testid={`prodotto-edit-qta-dep-${sp.id}`}
                                  />
                                  <span className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>{unitName(p.unita_misura_id)}</span>
                                </div>
                              ))}
                              <div className="border-t pt-2 flex justify-between text-sm" style={{ borderColor: "var(--ds-border)" }}>
                                <span className="label-tag">Totale</span>
                                <span className="font-semibold">{g.totalQta} {unitName(p.unita_misura_id)}</span>
                              </div>
                              <div className="text-[10px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                                Premi Invio o clicca fuori per salvare ogni riga.
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : editingCell?.id === p.id && editingCell?.field === "quantita" ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={editingCell.value}
                            onChange={(e) => setEditingCell({ ...editingCell, value: parseFloat(e.target.value) || 0 })}
                            className="h-8 w-20 rounded-md"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" onClick={saveCell}><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingCell(null)}><X size={14} /></Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => canEdit && setEditingCell({ id: p.id, field: "quantita", value: p.quantita })}
                          className="font-medium flex items-center gap-1 hover:underline disabled:no-underline disabled:cursor-default"
                          data-testid={`prodotto-edit-qta-${p.id}`}
                          style={{ color: isLow ? "var(--ds-danger)" : "var(--ds-text-primary)" }}
                          disabled={!canEdit}
                        >
                          {p.quantita}
                          {isLow && <AlertTriangle size={12} />}
                          {canEdit && <Pencil size={11} className="opacity-40" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{p.soglia_minima}</TableCell>
                    <TableCell className="font-medium text-sm">
                      {editingCell?.id === p.id && editingCell?.field === "prezzo_acquisto" ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={editingCell.value}
                            onChange={(e) => setEditingCell({ ...editingCell, value: parseFloat(e.target.value) || 0 })}
                            className="h-8 w-24 rounded-md"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" onClick={saveCell}><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingCell(null)}><X size={14} /></Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => canEdit && setEditingCell({ id: p.id, field: "prezzo_acquisto", value: p.prezzo_acquisto || 0 })}
                          className="flex items-center gap-1 hover:underline disabled:no-underline disabled:cursor-default"
                          data-testid={`prodotto-edit-pacq-${p.id}`}
                          disabled={!canEdit}
                        >
                          € {Number(p.prezzo_acquisto || 0).toFixed(2)}
                          {canEdit && <Pencil size={11} className="opacity-40" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-sm">
                      {editingCell?.id === p.id && editingCell?.field === "prezzo_vendita_con_iva" ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={editingCell.value}
                            onChange={(e) => setEditingCell({ ...editingCell, value: parseFloat(e.target.value) || 0 })}
                            className="h-8 w-24 rounded-md"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" onClick={saveCell}><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingCell(null)}><X size={14} /></Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => canEdit && setEditingCell({ id: p.id, field: "prezzo_vendita_con_iva", value: p.prezzo_vendita_con_iva || 0 })}
                          className="flex items-center gap-1 hover:underline disabled:no-underline disabled:cursor-default"
                          data-testid={`prodotto-edit-prezzo-${p.id}`}
                          disabled={!canEdit}
                        >
                          € {Number(p.prezzo_vendita_con_iva || 0).toFixed(2)}
                          {canEdit && <Pencil size={11} className="opacity-40" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {isMulti ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[var(--ds-bg)] transition-colors border"
                              style={{ borderColor: "var(--ds-border)", color: "var(--ds-primary)" }}
                              data-testid={`prodotto-multi-deps-${p.id}`}
                            >
                              <Shuffle size={13} />
                              <span className="text-xs font-medium">{g.products.length} depositi</span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-64 p-3">
                            <div className="label-tag mb-2">Suddivisione per deposito</div>
                            <div className="space-y-1.5">
                              {g.products.map((sp) => (
                                <div key={sp.id} className="flex justify-between items-center text-sm">
                                  <span className="truncate">{depositName(sp.deposito_id)}</span>
                                  <span className="font-medium ml-2">{sp.quantita}</span>
                                </div>
                              ))}
                              <div className="border-t pt-1.5 flex justify-between text-sm" style={{ borderColor: "var(--ds-border)" }}>
                                <span className="label-tag">Totale</span>
                                <span className="font-semibold">{g.totalQta}</span>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        depositName(p.deposito_id)
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{p.codice_barre || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {editingCell?.id === p.id && editingCell?.field === "fornitore_id" ? (
                        <div className="flex items-center gap-1">
                          <Select
                            value={editingCell.value || "none"}
                            onValueChange={(v) => setEditingCell({ ...editingCell, value: v === "none" ? "" : v })}
                          >
                            <SelectTrigger className="h-8 w-44 rounded-md">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nessuno</SelectItem>
                              {suppliersSorted.map((s) => <SelectItem key={s.id} value={s.id}>{s.ragione_sociale}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="icon" variant="ghost" onClick={saveCell}><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingCell(null)}><X size={14} /></Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => canEdit && setEditingCell({ id: p.id, field: "fornitore_id", value: p.fornitore_id || "" })}
                          className="flex items-center gap-1 hover:underline disabled:no-underline disabled:cursor-default"
                          data-testid={`prodotto-edit-fornitore-${p.id}`}
                          disabled={!canEdit}
                        >
                          {supplierName(p.fornitore_id)}
                          {canEdit && <Pencil size={11} className="opacity-40" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-[120px] truncate" title={p.nota}>{p.nota || "—"}</TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`prodotto-edit-${p.id}`}>
                          <Pencil size={14} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleteId(p.id)} data-testid={`prodotto-delete-${p.id}`}>
                          <Trash2 size={14} style={{ color: "var(--ds-danger)" }} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="prodotto-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-3xl font-light">
              {editing ? "Modifica prodotto" : "Nuovo prodotto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-6 pt-2">
            {/* Anagrafica */}
            <div>
              <div className="label-tag mb-3">Anagrafica</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="label-tag">Nome articolo *</Label>
                  <Input value={form.nome_articolo} onChange={(e) => updateForm({ nome_articolo: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="form-nome-articolo" />
                </div>
                <div>
                  <Label className="label-tag flex items-center gap-1"><Scan size={12} /> Codice a barre</Label>
                  <Input value={form.codice_barre} onChange={(e) => updateForm({ codice_barre: e.target.value })} placeholder="Manuale o scanner" className="mt-1.5 rounded-lg font-mono" data-testid="form-codice-barre" autoFocus={false} />
                </div>
                <div>
                  <Label className="label-tag">Codice articolo fornitore</Label>
                  <Input
                    value={form.codice_articolo_fornitore || ""}
                    onChange={(e) => updateForm({ codice_articolo_fornitore: e.target.value })}
                    placeholder="Es. SKU fornitore"
                    className="mt-1.5 rounded-lg font-mono"
                    data-testid="form-codice-articolo-fornitore"
                  />
                </div>
                <div>
                  <Label className="label-tag">Fornitore</Label>
                  <Select value={form.fornitore_id || "none"} onValueChange={(v) => {
                    if (v === "__add__") { setQuickSupplierOpen(true); return; }
                    updateForm({ fornitore_id: v === "none" ? "" : v });
                  }}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="form-fornitore">
                      <SelectValue placeholder="Seleziona…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      {suppliersSorted.map((s) => <SelectItem key={s.id} value={s.id}>{s.ragione_sociale}</SelectItem>)}
                      <SelectSeparator />
                      <SelectItem value="__add__" data-testid="form-add-fornitore-option">
                        <span className="inline-flex items-center gap-1.5" style={{ color: "var(--ds-primary)" }}>
                          <Plus size={12} /> Aggiungi fornitore
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-tag">Categoria *</Label>
                  <Select value={form.categoria_id} onValueChange={(v) => updateForm({ categoria_id: v })}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="form-categoria">
                      <SelectValue placeholder="Seleziona…" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Depositi & quantità */}
            <div>
              <div className="label-tag mb-3">Unità di magazzino & quantità</div>

              {/* Common fields always visible */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="label-tag">Unità di misura base *</Label>
                  <Select value={form.unita_misura_id} onValueChange={(v) => updateForm({ unita_misura_id: v })}>
                    <SelectTrigger className="mt-1.5 rounded-lg" data-testid="form-unita">
                      <SelectValue placeholder="Seleziona…" />
                    </SelectTrigger>
                    <SelectContent>
                      {baseUnits.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                    Es. pezzo, kg — è l'unità con cui il prodotto vive a magazzino. La confezione non è ammessa qui.
                  </div>
                </div>
                <div>
                  <Label className="label-tag">Soglia minima</Label>
                  <Input type="number" step="any" value={numVal(form.soglia_minima)}
                    onChange={(e) => updateForm({ soglia_minima: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                    className="mt-1.5 rounded-lg" />
                </div>
              </div>

              {/* Single-deposit mode */}
              {(editing || !multiMode) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="label-tag">Deposito *</Label>
                    <Select value={form.deposito_id} onValueChange={(v) => updateForm({ deposito_id: v })}>
                      <SelectTrigger className="mt-1.5 rounded-lg" data-testid="form-deposito">
                        <SelectValue placeholder="Seleziona…" />
                      </SelectTrigger>
                      <SelectContent>
                        {depositsSorted.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.preferito ? "★" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tag">Quantità *</Label>
                    <Input type="number" step="any" value={numVal(form.quantita)}
                      onChange={(e) => updateForm({ quantita: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      className="mt-1.5 rounded-lg" required data-testid="form-quantita" />
                  </div>
                </div>
              )}

              {/* Multi-deposit toggle BELOW the quantità field, full width row */}
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-3" data-testid="form-multi-deposito-toggle">
                <Checkbox
                  checked={multiMode}
                  onCheckedChange={(v) => { setMultiMode(!!v); if (!v) setMultiDepositi([]); }}
                />
                <span>{editing ? "Aggiungi questo prodotto anche in altri depositi" : "Disponibile in più depositi"}</span>
              </label>

              {/* Multi-deposit mode */}
              {multiMode && (
                <div className="space-y-3 mt-3 border-t pt-3" style={{ borderColor: "var(--ds-border)" }}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="rounded-lg w-full justify-start" data-testid="form-select-deposits">
                        <ChevronsRight size={14} className="mr-1.5" />
                        {multiDepositi.length > 0
                          ? `${multiDepositi.length} deposit${multiDepositi.length > 1 ? "i" : "o"} selezionat${multiDepositi.length > 1 ? "i" : "o"}`
                          : "Seleziona i depositi…"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 p-3">
                      <div className="label-tag mb-2">{editing ? "Aggiungi in altri depositi" : "Disponibile nei depositi"}</div>
                      <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {depositsSorted
                          .filter((d) => {
                            // in edit, exclude deposits where this product (same name+supplier) already exists
                            if (!editing) return true;
                            const others = list.filter(
                              (p) =>
                                p.id !== editing.id &&
                                (p.nome_articolo || "").trim().toLowerCase() === (form.nome_articolo || "").trim().toLowerCase() &&
                                (p.fornitore_id || "") === (form.fornitore_id || "")
                            );
                            return d.id !== form.deposito_id && !others.some((x) => x.deposito_id === d.id);
                          })
                          .map((d) => {
                          const sel = multiDepositi.find((x) => x.deposito_id === d.id);
                          return (
                            <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                              <Checkbox checked={!!sel} onCheckedChange={() => toggleMultiDeposit(d.id)} data-testid={`form-dep-checkbox-${d.id}`} />
                              <span className="flex-1">{d.nome} {d.preferito ? "★" : ""}</span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {multiDepositi.length > 0 && (
                    <div className="space-y-2 border rounded-lg p-3" style={{ borderColor: "var(--ds-border)" }}>
                      <div className="label-tag">Quantità per deposito *</div>
                      {multiDepositi.map((m) => {
                        const d = deposits.find((x) => x.id === m.deposito_id);
                        return (
                          <div key={m.deposito_id} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
                            <div className="text-sm">{d?.nome || "—"}</div>
                            <Input
                              type="number"
                              step="any"
                              value={numVal(m.quantita)}
                              onChange={(e) => setMultiDepQty(m.deposito_id, e.target.value === "" ? "" : parseFloat(e.target.value))}
                              placeholder="Q.tà"
                              className="rounded-lg"
                              data-testid={`form-dep-qty-${m.deposito_id}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleMultiDeposit(m.deposito_id)}
                              title="Rimuovi questo deposito"
                              data-testid={`form-dep-remove-${m.deposito_id}`}
                            >
                              <Trash2 size={14} style={{ color: "var(--ds-danger)" }} />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Prodotti per ordini — moltiplicatore confezione (opzionale) */}
            <div>
              <div className="label-tag mb-3">Prodotti per ordini</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-tag">Pezzi per confezione</Label>
                  <Input
                    type="number"
                    min="0"
                    value={numVal(form.pezzi_per_confezione)}
                    onChange={(e) => updateForm({ pezzi_per_confezione: e.target.value === "" ? "" : parseInt(e.target.value) })}
                    placeholder="es. 24"
                    className="mt-1.5 rounded-lg"
                    data-testid="form-pezzi"
                  />
                  <div className="text-[11px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                    Opzionale — quanti pezzi (o kg) contiene una confezione. Se lasciato vuoto, il prodotto <b>non</b> potrà essere ordinato a confezioni.
                    L'ordine a confezioni è riservato agli amministratori.
                  </div>
                </div>
              </div>
            </div>

            {/* Acquisto — Senza IVA prima (obbligatorio), poi IVA%, poi Con IVA */}
            <div>
              <div className="label-tag mb-3">Prezzo di acquisto</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="label-tag">Senza IVA (€) *</Label>
                  <Input type="number" step="0.01" value={numVal(form.prezzo_acquisto)} onChange={(e) => onPriceField("prezzo_acquisto", e.target.value)} className="mt-1.5 rounded-lg" required data-testid="form-pacq-net" />
                </div>
                <div>
                  <Label className="label-tag">IVA (%)</Label>
                  <Input type="number" step="0.01" value={numVal(form.iva_acquisto)} onChange={(e) => onPriceField("iva_acquisto", e.target.value)} className="mt-1.5 rounded-lg" />
                </div>
                <div>
                  <Label className="label-tag">Con IVA (€)</Label>
                  <Input type="number" step="0.01" value={numVal(form.prezzo_acquisto_con_iva)} onChange={(e) => onPriceField("prezzo_acquisto_con_iva", e.target.value)} className="mt-1.5 rounded-lg" data-testid="form-pacq-gross" />
                </div>
              </div>
            </div>

            {/* Vendita — Senza IVA prima, poi IVA%, poi Con IVA */}
            <div>
              <div className="label-tag mb-3">Prezzo di vendita</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="label-tag">Senza IVA (€)</Label>
                  <Input type="number" step="0.01" value={numVal(form.prezzo_vendita)} onChange={(e) => onPriceField("prezzo_vendita", e.target.value)} className="mt-1.5 rounded-lg" data-testid="form-pven-net" />
                </div>
                <div>
                  <Label className="label-tag">IVA (%)</Label>
                  <Input type="number" step="0.01" value={numVal(form.iva_vendita)} onChange={(e) => onPriceField("iva_vendita", e.target.value)} className="mt-1.5 rounded-lg" />
                </div>
                <div>
                  <Label className="label-tag">Con IVA (€)</Label>
                  <Input type="number" step="0.01" value={numVal(form.prezzo_vendita_con_iva)} onChange={(e) => onPriceField("prezzo_vendita_con_iva", e.target.value)} className="mt-1.5 rounded-lg" data-testid="form-pven-gross" />
                </div>
                <div>
                  <Label className="label-tag">Ricarico (%)</Label>
                  <Input type="number" step="0.01" value={numVal(form.ricarico)} readOnly className="mt-1.5 rounded-lg bg-[var(--ds-bg)]" />
                </div>
                <div>
                  <Label className="label-tag">Minimo d'ordine</Label>
                  <Input type="number" step="any" value={numVal(form.minimo_ordine)}
                    onChange={(e) => updateForm({ minimo_ordine: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                    className="mt-1.5 rounded-lg" data-testid="form-minimo-ordine" />
                </div>
              </div>
            </div>

            <div>
              <Label className="label-tag">Nota</Label>
              <Textarea value={form.nota || ""} onChange={(e) => updateForm({ nota: e.target.value })} className="mt-1.5 rounded-lg" rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="prodotto-submit">
                {editing ? "Salva modifiche" : "Crea prodotto"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il prodotto?</AlertDialogTitle>
            <AlertDialogDescription>Verranno rimossi anche i movimenti e lo storico prezzi.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }} data-testid="prodotto-confirm-delete">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selectedIds.size} prodotti?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno rimossi anche tutti i movimenti e gli storici prezzi associati. Operazione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} style={{ background: "var(--ds-danger)" }} data-testid="prodotti-bulk-confirm">
              Elimina tutti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="prodotti-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-3xl font-light">Importa prodotti da XLS</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
              Il file deve avere le seguenti colonne (la prima riga è l'intestazione, le altre sono i prodotti).
              Categorie, depositi, unità di misura e fornitori sono identificati per <b>nome</b> e devono già esistere nel sistema.
            </div>
            <div className="text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-secondary)" }}>
              <b>Obbligatori:</b> nome_articolo, categoria, deposito, unita_misura, quantita,
              prezzo_acquisto_con_iva
              <br />
              <b>Opzionali:</b> codice_barre, fornitore, soglia_minima, iva_acquisto, prezzo_vendita_con_iva, iva_vendita, minimo_ordine, pezzi_per_confezione, nota
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={downloadTemplate} className="rounded-lg" data-testid="prodotti-template-inline">
                <Download size={14} className="mr-1.5" /> Scarica template
              </Button>
              <label className="ds-btn-primary inline-flex items-center cursor-pointer rounded-lg px-4 py-2 text-sm" style={{ background: "var(--ds-primary)", color: "white" }}>
                <Upload size={14} className="mr-1.5" />
                {importBusy ? "Caricamento…" : "Scegli file .xlsx"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} disabled={importBusy} data-testid="prodotti-import-input" />
              </label>
            </div>

            {importResult && (
              <div className="space-y-2">
                <div className="text-sm">
                  <b style={{ color: "var(--ds-success, #7B8B6F)" }}>Creati: {importResult.created}</b>
                  {" · "}
                  <b style={{ color: importResult.skipped?.length ? "var(--ds-danger)" : "inherit" }}>
                    Saltati: {importResult.skipped?.length || 0}
                  </b>
                  {" · "}
                  Totale righe: {importResult.total_rows}
                </div>
                {importResult.skipped?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                    {importResult.skipped.map((s, i) => (
                      <div key={`skip-${s.row || i}`} className="py-1 border-b last:border-b-0" style={{ borderColor: "var(--ds-border)" }}>
                        <b>Riga {s.row}:</b> {s.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-update dialog */}
      <Dialog open={bulkUpdateOpen} onOpenChange={setBulkUpdateOpen}>
        <DialogContent className="max-w-2xl" data-testid="prodotti-bulk-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-3xl font-light">Modifica massiva da XLS</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
              Esporta i prodotti, modificali sul foglio Excel mantenendo la colonna <b>id</b>, poi reimporta.
              I prodotti vengono aggiornati per id.
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={downloadExport} className="rounded-lg" data-testid="bulk-update-export">
                <Download size={14} className="mr-1.5" /> Esporta prodotti attuali
              </Button>
              <label className="ds-btn-primary inline-flex items-center cursor-pointer rounded-lg px-4 py-2 text-sm" style={{ background: "var(--ds-primary)", color: "white" }}>
                <Upload size={14} className="mr-1.5" />
                {bulkUpdateBusy ? "Aggiornamento…" : "Scegli file modificato"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpdate} disabled={bulkUpdateBusy} data-testid="bulk-update-input" />
              </label>
            </div>
            {bulkUpdateResult && (
              <div className="space-y-2">
                <div className="text-sm">
                  <b style={{ color: "#7B8B6F" }}>Aggiornati: {bulkUpdateResult.updated}</b>
                  {" · "}
                  <b style={{ color: bulkUpdateResult.skipped?.length ? "var(--ds-danger)" : "inherit" }}>
                    Saltati: {bulkUpdateResult.skipped?.length || 0}
                  </b>
                </div>
                {bulkUpdateResult.skipped?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                    {bulkUpdateResult.skipped.map((s, i) => (
                      <div key={`bu-skip-${s.row || i}`} className="py-1 border-b last:border-b-0" style={{ borderColor: "var(--ds-border)" }}>
                        <b>Riga {s.row}:</b> {s.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUpdateOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Quick-add supplier */}
      <Dialog open={quickSupplierOpen} onOpenChange={setQuickSupplierOpen}>
        <DialogContent data-testid="quick-fornitore-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">Aggiungi fornitore rapido</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitQuickSupplier} className="space-y-3 pt-2">
            <div>
              <Label className="label-tag">Ragione sociale *</Label>
              <Input value={quickSupplier.ragione_sociale} onChange={(e) => setQuickSupplier({ ...quickSupplier, ragione_sociale: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-ragione" />
            </div>
            <div>
              <Label className="label-tag">Nome referente *</Label>
              <Input value={quickSupplier.nome} onChange={(e) => setQuickSupplier({ ...quickSupplier, nome: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-nome" />
            </div>
            <div>
              <Label className="label-tag">Email *</Label>
              <Input type="email" value={quickSupplier.email} onChange={(e) => setQuickSupplier({ ...quickSupplier, email: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-email" />
            </div>
            <div>
              <Label className="label-tag">Telefono *</Label>
              <Input value={quickSupplier.telefono} onChange={(e) => setQuickSupplier({ ...quickSupplier, telefono: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="quick-forn-telefono" />
            </div>
            <div className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
              I dettagli completi (P.IVA, indirizzo…) si aggiungono dalla pagina Fornitori.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuickSupplierOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={quickSupplierBusy} className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="quick-forn-submit">
                {quickSupplierBusy ? "Salvataggio…" : "Crea e seleziona"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
